/**
 * REWRITE: prestige at Depth 50. Burn the file — XP, Depth, Scrip, the
 * nodes and the loadout go — keep the stamps and the glyph's age, earn
 * Wakelight. Wakelight buys CRT themes, alias slots and preset slots.
 * Never power: nothing here touches a stat.
 */
import type { Account } from "../progression/account";
import { stampsWord } from "../identity/identity";
import { DEFAULT_LOADOUT } from "../manifest/loadout";
import { emptyMasteries } from "../progression/mastery";
import { MAX_DEPTH } from "../progression/depth";

export const REWRITE_WAKELIGHT = 500;

export interface CosmeticDef {
  id: string;
  kind: "theme" | "alias" | "preset";
  name: string;
  line: string;
  wakelight: number;
  /** themes: the HUD palette (cyan, green, magenta, yellow, amber) */
  palette?: { cy: string; gr: string; mg: string; ye: string; am: string };
}

export const COSMETICS: readonly CosmeticDef[] = [
  { id: "theme_phosphor", kind: "theme", name: "PHOSPHOR", line: "GREEN-ON-BLACK TERMINAL, THE FIRST CRT YOU EVER SAW", wakelight: 120, palette: { cy: "#7dffb0", gr: "#37ff8b", mg: "#c8ff5a", ye: "#e9ffb0", am: "#9dff6e" } },
  { id: "theme_amber", kind: "theme", name: "AMBER", line: "THE ESTATE'S OWN MONITORS", wakelight: 120, palette: { cy: "#ffd27a", gr: "#ffb02e", mg: "#ff8a3c", ye: "#ffe34a", am: "#ff9a1e" } },
  { id: "theme_ice", kind: "theme", name: "ICE", line: "DEADLETTER DOCKS IN JANUARY", wakelight: 160, palette: { cy: "#bfefff", gr: "#8fd8ff", mg: "#d9b8ff", ye: "#ffffff", am: "#a8c8ff" } },
  { id: "theme_bloodline", kind: "theme", name: "BLOODLINE", line: "KERNEL RED ON BLACK; FOR FILES THAT HAVE TAKEN THE CHAIR", wakelight: 300, palette: { cy: "#ff6b7a", gr: "#ff1e3c", mg: "#ff3ec9", ye: "#ffd6da", am: "#ff5a3c" } },
  { id: "alias_2", kind: "alias", name: "ALIAS SLOT II", line: "A SECOND SAVED NAME THE CITY MAY CALL YOU", wakelight: 90 },
  { id: "alias_3", kind: "alias", name: "ALIAS SLOT III", line: "A THIRD", wakelight: 140 },
  { id: "preset_2", kind: "preset", name: "PRESET SLOT II", line: "A SECOND SAVED LOADOUT", wakelight: 60 },
  { id: "preset_3", kind: "preset", name: "PRESET SLOT III", line: "A THIRD", wakelight: 90 },
  { id: "preset_4", kind: "preset", name: "PRESET SLOT IV", line: "A FOURTH", wakelight: 120 },
  { id: "preset_5", kind: "preset", name: "PRESET SLOT V", line: "A FIFTH", wakelight: 160 },
];

export const cosmeticById = (id: string): CosmeticDef | undefined => COSMETICS.find((c) => c.id === id);

export function canRewrite(a: Account): { ok: boolean; reason?: string } {
  if (a.depth < MAX_DEPTH) return { ok: false, reason: `DEPTH ${a.depth} — REWRITE OPENS AT ${MAX_DEPTH}` };
  return { ok: true };
}

/** Burn the file. Keeps: id, name, stamps, counters (the glyph's age), moniker, chapters, cosmetics, campaign. */
export function rewrite(a: Account): { ok: boolean; reason?: string; wakelight?: number } {
  const can = canRewrite(a);
  if (!can.ok) return can;
  a.rewrites = (a.rewrites ?? 0) + 1;
  a.xp = 0;
  a.depth = 1;
  a.wallet.scrip = 0;
  a.wallet.salvage = 0;
  a.owned = a.owned.filter((id) => id.startsWith("weapon:")); // campaign weapon unlocks are earned, not bought
  a.loadout = { ...DEFAULT_LOADOUT, attested: [], keystone: null, chips: {}, firmware: {} };
  a.mastery = emptyMasteries();
  a.wears = [];
  a.matches = 0;
  a.debt = null;
  a.wallet.wakelight += REWRITE_WAKELIGHT;
  a.counters["rewrites"] = a.rewrites;
  a.ledger.push(`REWRITE ${a.rewrites} · THE FILE BURNS · ${stampsWord(a.stamps.length)} KEPT · +${REWRITE_WAKELIGHT} WAKELIGHT`);
  return { ok: true, wakelight: REWRITE_WAKELIGHT };
}

/**
 * Slots a file has: one alias and one preset for free, more from the shop.
 *
 * Read as the highest slot owned rather than as a count of ids (Stage 176). For anything bought in
 * the shop the two are the same number, because `buyCosmetic` refuses to sell slot n before slot
 * n-1 and the ids are therefore contiguous. They come apart for the Deep Wake pass, which grants
 * `alias_4` and `preset_6` — one past the top of the shop, and the only way to reach either. An id
 * that says it is the fourth slot has to mean the file has four.
 */
/** One slot is SLOT, not SLOTS. */
export function slotsWord(n: number): string {
  return `${n} SLOT${n === 1 ? "" : "S"}`;
}

/** A write into a slot the file does not have. */
export function slotNotOwned(slot: number, have: number): string {
  return `SLOT ${slot} NOT OWNED (${slotsWord(have)})`;
}

export function slotsOf(a: Account): { aliases: number; presets: number } {
  const owned = a.cosmetics ?? [];
  const top = (kind: string) => Math.max(1, ...owned.filter((id) => id.startsWith(`${kind}_`)).map((id) => Number(id.slice(kind.length + 1))).filter(Number.isFinite));
  return { aliases: top("alias"), presets: top("preset") };
}

export function buyCosmetic(a: Account, id: string): { ok: boolean; reason?: string } {
  const c = cosmeticById(id);
  if (!c) return { ok: false, reason: "UNKNOWN COSMETIC" };
  a.cosmetics = a.cosmetics ?? [];
  if (a.cosmetics.includes(id)) return { ok: false, reason: "ALREADY OWNED" };
  // slots come in order, and a slot the file already has is not for sale: a Deep Wake pass grants
  // the top slot outright, and selling the ones underneath it afterwards would charge Wakelight
  // for nothing (Stage 176)
  if (c.kind !== "theme") {
    const n = Number(id.split("_")[1]);
    const have = c.kind === "alias" ? slotsOf(a).aliases : slotsOf(a).presets;
    if (n <= have) return { ok: false, reason: `${c.kind.toUpperCase()} SLOT ${n} ALREADY OWNED (${have} SLOTS)` };
    if (n > 2 && !a.cosmetics.includes(`${c.kind}_${n - 1}`)) return { ok: false, reason: `NEEDS ${c.kind.toUpperCase()} SLOT ${n - 1} FIRST` };
  }
  if (a.wallet.wakelight < c.wakelight) return { ok: false, reason: `NEEDS ${c.wakelight} WAKELIGHT` };
  a.wallet.wakelight -= c.wakelight;
  a.cosmetics.push(id);
  a.ledger.push(`WAKELIGHT · ${c.name} · −${c.wakelight}`);
  return { ok: true };
}

export function setTheme(a: Account, id: string | null): boolean {
  if (id && !(a.cosmetics ?? []).includes(id)) return false;
  a.theme = id;
  return true;
}

/** Save a loadout preset into an owned slot (raw JSON; validated like any loadout when it is applied). */
export function savePreset(a: Account, slot: number, name: string, loadout: unknown): { ok: boolean; reason?: string } {
  const slots = slotsOf(a).presets;
  if (slot < 1 || slot > slots) return { ok: false, reason: slotNotOwned(slot, slots) };
  a.presets = a.presets ?? [];
  a.presets[slot - 1] = { name: String(name).slice(0, 24).toUpperCase() || `PRESET ${slot}`, loadout: JSON.parse(JSON.stringify(loadout ?? {})) };
  return { ok: true };
}

export function setAlias(a: Account, slot: number, alias: string): { ok: boolean; reason?: string } {
  const slots = slotsOf(a).aliases;
  if (slot < 1 || slot > slots) return { ok: false, reason: slotNotOwned(slot, slots) };
  const clean = alias.replace(/[^\x20-\x7e]/g, "").trim().slice(0, 16).toUpperCase();
  if (!clean) return { ok: false, reason: "EMPTY ALIAS" };
  a.aliases = a.aliases ?? [];
  a.aliases[slot - 1] = clean;
  return { ok: true };
}
